import React, { useState, useEffect } from 'react';
import { leaderboardAPI } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const Leaderboard = () => {
  const [loading, setLoading] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);

  useEffect(() => {
    loadLeaderboard();
    loadUserRank();
  }, []);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await leaderboardAPI.getOverall('all_time');
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserRank = async () => {
    try {
      const response = await leaderboardAPI.getMyRank();
      setUserRank(response.data.rank);
    } catch (error) {
      console.error('Failed to load user rank:', error);
    }
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'text-yellow-500 bg-yellow-100';
    if (rank === 2) return 'text-gray-500 bg-gray-100';
    if (rank === 3) return 'text-orange-600 bg-orange-100';
    return 'text-blue-600 bg-blue-100';
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return '';
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const toggleUserDetails = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  return (
    <div className="max-w-6xl mx-auto py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">All-Time Leaderboard</h1>
        <p className="text-gray-600">See how you rank among MIND DETOX community</p>
      </div>

      {/* User Rank */}
      {userRank && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white mb-8">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">Your Current Rank</h2>
            <div className="flex items-center justify-center space-x-3">
              <span className="text-4xl font-bold">#{userRank.rank || '--'}</span>
              <span className="text-blue-100">Overall</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div>
              <p className="text-2xl font-bold">#{userRank.weekly}</p>
              <p className="text-blue-100 text-sm">Weekly</p>
            </div>
            <div>
              <p className="text-2xl font-bold">#{userRank.monthly}</p>
              <p className="text-blue-100 text-sm">Monthly</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{userRank.score || 0}</p>
              <p className="text-blue-100 text-sm">Points</p>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Top Performers</h2>
        
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="space-y-3">
            {leaderboard.map((entry) => (
              <div
                key={entry.user.id}
                className={`border-2 rounded-lg transition-all ${
                  entry.rank === 1 ? 'border-yellow-300 bg-yellow-50' :
                  entry.rank === 2 ? 'border-gray-300 bg-gray-50' :
                  entry.rank === 3 ? 'border-orange-300 bg-orange-50' :
                  'border-gray-200 hover:border-blue-200 hover:bg-blue-50'
                }`}
              >
                {/* Main entry */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => toggleUserDetails(entry.user.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${getRankColor(entry.rank)}`}>
                        {getRankIcon(entry.rank) || entry.rank}
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                          {entry.user.profile_picture_url ? (
                            <img 
                              src={entry.user.profile_picture_url} 
                              alt={entry.user.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-gray-600 font-medium">
                              {entry.user.name?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          )}
                        </div>
                        
                        <div>
                          <h3 className="font-semibold text-gray-900">{entry.user.name}</h3>
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <span>Level {Math.floor(entry.score / 100) + 1}</span>
                            {entry.user.country && (
                              <>
                                <span>•</span>
                                <span>{entry.user.country}</span>
                              </>
                            )}
                            <span>•</span>
                            <span className={getHealthScoreColor(entry.organ_health_score)}>
                              {entry.organ_health_score}% Health
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">{entry.score}</div>
                      <p className="text-sm text-gray-600">points</p>
                    </div>
                  </div>
                </div>

                {/* Expanded details */}
                {expandedUser === entry.user.id && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {entry.organ_health_records || 0}
                        </div>
                        <p className="text-sm text-gray-600">Health Records</p>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {entry.organ_health_records || 0}
                        </div>
                        <p className="text-sm text-gray-600">Health Records</p>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {Math.round(entry.organ_health_score || 0)}%
                        </div>
                        <p className="text-sm text-gray-600">Health Score</p>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {entry.rank || '--'}
                        </div>
                        <p className="text-sm text-gray-600">Rank</p>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-yellow-600">
                          {entry.user.country || 'Unknown'}
                        </div>
                        <p className="text-sm text-gray-600">Country</p>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Joined: {new Date(entry.user.created_at).toLocaleDateString()}</span>
                        <span>Last Login: {entry.user.last_login ? new Date(entry.user.last_login).toLocaleDateString() : 'Never'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
